import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import courseService from '../../../../services/mentor/courseService';
import batchService from '../../../../services/mentor/batchService';
import type { ICourse } from '../../../../types/course';
import type { IBatch } from '../../../../types/batch';
import Loader from '../../../../components/common/Loader';
import { showToast } from '../../../../utils/showToast';
import { Layers, Plus, Eye, Edit, Trash2, ArrowLeft, BookOpen } from 'lucide-react';

// Lazy load modals for better performance
const CreateBatchModal = lazy(() => import('./CreateBatchModal'));
const EditBatchModal = lazy(() => import('./EditBatchModal'));

const CourseDetail: React.FC = () => {
    const { courseId } = useParams<{ courseId: string }>();
    const navigate = useNavigate();
    const [course, setCourse] = useState<ICourse | null>(null);
    const [batches, setBatches] = useState<IBatch[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    
    // State for modals
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedBatch, setSelectedBatch] = useState<IBatch | null>(null);

    const fetchCourseDetails = async () => {
        if (!courseId) return;
        setIsLoading(true);
        try {
            const { course: courseData, batches: batchesData } = await courseService.getCourseDetailsForMentor(courseId);
            setCourse(courseData);
            setBatches(batchesData);
        } catch (err: any) {
            showToast(err.message, { type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchCourseDetails();
    }, [courseId]);
    
    const handleOpenEditModal = (batch: IBatch) => {
        setSelectedBatch(batch);
        setIsEditModalOpen(true);
    };

    const handleDeleteBatch = async (batch: IBatch) => {
        if (!window.confirm(`Are you sure you want to delete the batch "${batch.name}"?`)) return;
        try {
            await batchService.deleteBatch(batch._id);
            showToast(`Batch "${batch.name}" deleted.`, { type: 'success' });
            fetchCourseDetails(); // Refetch to update the list
        } catch (err: any) {
            showToast(err.message, { type: 'error' });
        }
    };

    if (isLoading) return <Loader />;
    if (!course) return <p className="text-center text-red-500">Course not found.</p>;

    return (
        <>
            <Helmet>
                <title>Manage: {course.title}</title>
            </Helmet>
            <div className="space-y-6">
                <Link to="/dashboard/mentor/manage-courses" className="inline-flex items-center gap-2 text-blue-600 hover:underline font-medium">
                    <ArrowLeft size={16} /> Back to All Courses
                </Link>
                
                <header className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                    <h1 className="text-3xl font-bold text-gray-800">{course.title}</h1>
                    <p className="text-md text-gray-500 mt-2">{course.description}</p>
                </header>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
                            <Layers /> Batches
                        </h2>
                        <button onClick={() => setIsCreateModalOpen(true)} className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2.5 rounded-full text-sm font-medium hover:bg-blue-700 flex items-center justify-center gap-2">
                            <Plus size={16} /> Create New Batch
                        </button>
                    </div>
                    
                    {batches.length === 0 ? (
                         <div className="text-center py-12">
                            <BookOpen className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-lg font-medium text-gray-900">No Batches Found</h3>
                            <p className="mt-1 text-sm text-gray-500">Click "Create New Batch" to get started.</p>
                        </div>
                    ) : (
                        <ul className="divide-y divide-gray-200">
                            {batches.map(batch => (
                                <li key={batch._id} className="py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div>
                                        <p className="text-md font-medium text-gray-900">{batch.name}</p>
                                        <p className="text-sm text-gray-500">
                                            {batch.month} {batch.year} | Students: {batch.enrollments?.length || 0}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <button onClick={() => navigate(`/dashboard/mentor/batches/${batch._id}`)} className="p-2 text-gray-500 hover:bg-gray-100 rounded-full" title="View Students">
                                            <Eye size={18} />
                                        </button>
                                        <button onClick={() => handleOpenEditModal(batch)} className="p-2 text-blue-600 hover:bg-blue-100 rounded-full" title="Edit Batch">
                                            <Edit size={18} />
                                        </button>
                                        <button onClick={() => handleDeleteBatch(batch)} className="p-2 text-red-600 hover:bg-red-100 rounded-full" title="Delete Batch">
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            <Suspense fallback={null}>
                {isCreateModalOpen && courseId && (
                    <CreateBatchModal
                        isOpen={isCreateModalOpen}
                        onClose={() => setIsCreateModalOpen(false)}
                        onSuccess={fetchCourseDetails}
                        courseId={courseId}
                    />
                )}
                {isEditModalOpen && selectedBatch && (
                    <EditBatchModal
                        isOpen={isEditModalOpen}
                        onClose={() => setIsEditModalOpen(false)}
                        onSuccess={fetchCourseDetails}
                        batch={selectedBatch}
                    />
                )}
            </Suspense>
        </>
    );
};

export default CourseDetail;